import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CircleIcon from '@mui/icons-material/Circle';
import { Accordion, AccordionDetails, AccordionSummary, Grid, List, ListItem, ListItemIcon, ListItemText, Typography, alpha } from '@mui/material';
import { SidebarNote } from 'features/augmented-writing/augmented-writing.types';
import _ from 'lodash';
import React from 'react';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';

interface Props {
  sidebarNote: SidebarNote;
  expanded: string | false;
  disableBorder?: boolean;

  handleExpandNote(key: string, sidebarNote: SidebarNote): () => void;
}

const NoteItem: React.FC<Props> = ({ sidebarNote, expanded, disableBorder, handleExpandNote }) => {
  const { classes, cx } = useStyles({ noteKey: sidebarNote.key, expanded, disableBorder });

  const generateList = (words: string[]) => {
    return (
      <Grid container={true}>
        {_.take(words, 20).map((word, index) => (
          <Grid key={index} size={6}>
            <ListItem className={classes.listItem}>
              <ListItemIcon className={classes.listItemIcon}>
                <CircleIcon className={classes.listIcon} />
              </ListItemIcon>
              <ListItemText classes={{ primary: classes.listText }} primary={word} />
            </ListItem>
          </Grid>
        ))}
      </Grid>
    );
  };

  if (!_.isNil(sidebarNote.title) && !_.isNil(sidebarNote.key)) {
    return (
      <Accordion className={classes.sidebarNote} expanded={expanded === sidebarNote.key} disableGutters={true} elevation={0}>
        <AccordionSummary className={classes.summary} expandIcon={undefined} onClick={handleExpandNote(sidebarNote.key, sidebarNote)}>
          <ArrowForwardIcon className={classes.icon} style={{ marginRight: '8px' }} />
          <Typography className={classes.sidebarNoteText}>{sidebarNote.text}</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <Typography className={classes.sidebarNoteTitle}>{sidebarNote.title}</Typography>
          {sidebarNote.addwords && sidebarNote.addwords.length > 0 && <List className={classes.list}>{generateList(sidebarNote.addwords)}</List>}
        </AccordionDetails>
      </Accordion>
    );
  } else {
    return (
      <div className={cx(classes.sidebarNote, classes.noTitle)}>
        <Typography className={classes.sidebarNoteText}>{sidebarNote.text}</Typography>
      </div>
    );
  }
};

const useStyles = makeStyles<{ noteKey: string | undefined; expanded: string | false; disableBorder?: boolean }>()(
  (theme, { noteKey, expanded, disableBorder }) => ({
    sidebarNote: {
      display: expanded && noteKey !== expanded ? 'none' : undefined,
      borderRadius: 0,
      background: '#FFF',
      boxShadow: disableBorder ? undefined : Shadows.GREY_SHADOW_LIGHTER,
      '&.MuiPaper-root::before': {
        background: '#FFF',
      },
      '&:last-of-type': {
        borderRadius: 0,
      },
    },
    summary: {
      '& .MuiAccordionSummary-content': {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
      },
      '&:hover': {
        background: alpha(theme.palette.primary.main, 0.1),
      },
    },
    details: {
      padding: '4px 16px 16px 16px',
    },
    noTitle: {
      minHeight: '24px',
      display: expanded && noteKey !== expanded ? 'none' : 'flex',
      alignItems: 'center',
      padding: '12px 16px',
    },
    sidebarNoteText: {
      fontSize: theme.typography.pxToRem(11),
    },
    sidebarNoteTitle: {
      fontSize: theme.typography.pxToRem(11),
      fontWeight: 'bold',
    },
    howText: {
      fontSize: theme.typography.pxToRem(11),
      fontWeight: 700,
      color: theme.palette.secondary.main,
    },
    list: {
      padding: 0,
      margin: 0,
      marginTop: '8px',
    },
    listItem: {
      padding: '0 16px 0 0',
    },
    listItemIcon: {
      minWidth: 0,
      marginRight: '4px',
    },
    listIcon: {
      color: theme.palette.primary.main,
      fontSize: theme.typography.pxToRem(4),
    },
    listText: {
      fontSize: theme.typography.pxToRem(10),
    },
    icon: {
      fontSize: theme.typography.pxToRem(16),
      color: theme.palette.primary.main,
    },
  })
);

export default NoteItem;
